import React, { forwardRef, memo, useEffect, useId, useRef, useState } from 'react';
import { Box, Checkbox, Text, useMergeRefs, useToast } from '@chakra-ui/react';
import { Campaign, Collab, GoLiveWindowDto, PRE_ACCEPT_STATUSES, deadlineKindFor, goLiveLabel, isPostByPassed, isValidYmd, windowOf } from '@foodfluence/shared';
import { getBrowserTimezone } from 'utils/timezoneUtils';
import { goLiveLabels } from '../utils/postByLabels';

type GoLiveCampaignFields = Pick<Campaign, 'id' | 'contentGoLiveStart' | 'contentGoLiveEnd' | 'requireDraftApproval' | 'creatorType' | 'contentRequirements'>;

const asWindow = (v: unknown): GoLiveWindowDto | null => {
  const w = v as Partial<GoLiveWindowDto> | null | undefined;
  return isValidYmd(w?.start) && isValidYmd(w?.end) && w!.start! <= w!.end! ? { start: w!.start!, end: w!.end! } : null;
};
const sameWindow = (a: GoLiveWindowDto | null | undefined, b: GoLiveWindowDto | null | undefined): boolean =>
  !!a && !!b && a.start === b.start && a.end === b.end;

/** The window the server last reported per campaign (GO_LIVE_CHANGED): fresher than a cached doc. */
const latestGoLive = new Map<string, GoLiveWindowDto>();

/**
 * The window the creator confirmed on the review page, carried to whichever accept step follows
 * (product selection, scheduling, retail). Per tab, keyed by collab.
 */
const confirmationKey = (collabId: string) => `goLiveConfirmed:${collabId}`;
export const rememberGoLiveConfirmation = (collabId: string, window: GoLiveWindowDto | undefined): void => {
  try {
    if (window) sessionStorage.setItem(confirmationKey(collabId), JSON.stringify(window));
    else sessionStorage.removeItem(confirmationKey(collabId));
  } catch {
    // Storage blocked: the step page shows the checkbox again.
  }
};
/** The confirmed window: react-router navigation state first (survives blocked storage), then sessionStorage (survives a reload). */
export const getGoLiveConfirmation = (collabId: string, navState?: unknown): GoLiveWindowDto | undefined => {
  const carried = asWindow((navState as { goLiveConfirmed?: unknown } | null | undefined)?.goLiveConfirmed);
  if (carried) return carried;
  try {
    const stored = sessionStorage.getItem(confirmationKey(collabId));
    return (stored && asWindow(JSON.parse(stored))) || undefined;
  } catch {
    return undefined;
  }
};

type GoLiveErrorCode = 'GO_LIVE_CHANGED' | 'GO_LIVE_PASSED' | 'GO_LIVE_UNREACHABLE';
type ApiError = { code?: string; goLive?: unknown; message?: string };

/** The go-live refusal code the server attached to a failed accept/proposal, if any. */
export const goLiveErrorCode = (error: unknown): GoLiveErrorCode | null => {
  const code = (error as ApiError | null)?.code;
  return code === 'GO_LIVE_CHANGED' || code === 'GO_LIVE_PASSED' || code === 'GO_LIVE_UNREACHABLE' ? code : null;
};

/** The server refused an accept because every spot on the campaign is taken. */
export const isCampaignFullError = (error: unknown): boolean => (error as ApiError | null)?.code === 'CAMPAIGN_FULL';

/**
 * The server's own wording for a date it refused against go-live (posting day outside the window,
 * visit after the visit deadline): it names the allowed dates. Null for any other error.
 */
export const goLiveDateRefusalMessage = (error: unknown): string | null => {
  const code = (error as ApiError | null)?.code;
  return (code === 'POSTING_DATE_OUTSIDE_GO_LIVE' || code === 'VISIT_AFTER_DEADLINE') && error instanceof Error ? error.message : null;
};

export interface PostByConfirmState {
  /** The campaign has go-live and the collab isn't locked to other terms: the checkbox is needed. */
  required: boolean;
  goLive?: GoLiveWindowDto;
  /** "I can post on Oct 28" and the note under it. */
  labels?: { confirm: string; note: string };
  confirmed: boolean;
  /** Go-live has ended (browser timezone) or the server said so. */
  passed: boolean;
  /** The server said go-live can no longer be met (GO_LIVE_UNREACHABLE). */
  unreachable: boolean;
  /** The server reported new dates this session (GO_LIVE_CHANGED). */
  changed: boolean;
  setConfirmed: (checked: boolean) => void;
  /** Why the primary action is disabled, or null when go-live doesn't block it. */
  blockedReason: string | null;
  /** The value to send as `goLiveConfirmed` (undefined when not required or not confirmed). */
  goLiveConfirmed?: GoLiveWindowDto;
  /** Handles a GO_LIVE_* or CAMPAIGN_FULL refusal (toast; new dates / disables). False for any other error. */
  handleError: (error: unknown) => boolean;
  /** Bumped on each GO_LIVE_CHANGED refusal: the checkbox takes focus when it changes. */
  focusRequest: number;
}

/**
 * Go-live confirmation for an accept, proposal or counter. Required only when the campaign has a
 * go-live window and the collab has no locked terms (the server skips the check then).
 * `initiallyConfirmed`: a window ticked on an earlier page (accept step pages); it counts while it
 * still matches.
 */
export const usePostByConfirm = (
  campaign: GoLiveCampaignFields | null | undefined,
  collab?: Collab | null,
  initiallyConfirmed?: GoLiveWindowDto
): PostByConfirmState => {
  const toast = useToast();
  const locked = collab?.contentTurnaroundDays != null;
  const campaignWindow = locked ? null : windowOf(campaign);
  const [serverWindow, setServerWindow] = useState<GoLiveWindowDto | null>(null);
  // This page's server refusal counts too: a campaign that added go-live since it was loaded still
  // gets the box. (Not latestGoLive alone: a window the brand has since removed would stick.)
  const required = !locked && !!(serverWindow ?? campaignWindow);
  // A locked collab not yet accepted keeps its own window and needs no tick, but the server still
  // refuses it once that window has ended (GO_LIVE_PASSED).
  const ownWindow = locked && collab && PRE_ACCEPT_STATUSES.includes(collab.status) ? windowOf(collab) : null;
  // The window the server refused as passed / unreachable: a different one clears it.
  const [serverBlocked, setServerBlocked] = useState<{ window: GoLiveWindowDto; code: GoLiveErrorCode } | null>(null);
  // Holds the window that was ticked, so changed dates untick the box on their own.
  const [confirmedFor, setConfirmedFor] = useState<GoLiveWindowDto | null>(initiallyConfirmed ?? null);
  const [focusRequest, setFocusRequest] = useState(0);

  const goLive = required
    ? serverWindow ?? (campaign?.id ? latestGoLive.get(campaign.id) : undefined) ?? campaignWindow!
    : undefined;
  const blockedHere = goLive && sameWindow(serverBlocked?.window, goLive) ? serverBlocked!.code : null;
  // Browser timezone here; the server checks the store's/creator's and has the final word.
  const passed = !!goLive && (blockedHere === 'GO_LIVE_PASSED' || isPostByPassed({ contentGoLiveStart: goLive.start, contentGoLiveEnd: goLive.end }, new Date(), getBrowserTimezone()));
  const unreachable = blockedHere === 'GO_LIVE_UNREACHABLE';
  const confirmed = sameWindow(confirmedFor, goLive);
  const ownPassed = !!ownWindow && (
    (serverBlocked?.code === 'GO_LIVE_PASSED' && sameWindow(serverBlocked.window, ownWindow)) ||
    isPostByPassed(collab, new Date(), getBrowserTimezone())
  );

  let blockedReason: string | null = null;
  if (ownPassed) blockedReason = "This collab's go-live has passed.";
  else if (goLive) {
    if (passed) blockedReason = "This campaign's go-live has passed.";
    else if (unreachable) blockedReason = "There isn't enough time left before this campaign's go-live, so it can't be accepted any more.";
    else if (!confirmed) blockedReason = `Tick the box to confirm you can post ${goLiveLabel(goLive, 'short')}.`;
  }

  const handleError = (error: unknown): boolean => {
    // Every accept routes its failure through here, so the full-campaign refusal is handled once.
    if (isCampaignFullError(error)) {
      toast({ title: 'This collab is full', description: 'Every spot has been taken. You can ask the brand to add you to the waitlist.', status: 'error', duration: 6000, isClosable: true });
      return true;
    }
    const code = goLiveErrorCode(error);
    if (!code) return false;
    const { message } = error as ApiError;
    const window = asWindow((error as ApiError).goLive) ?? goLive ?? null;
    if (window) setServerWindow(window);
    if (code === 'GO_LIVE_CHANGED') {
      // Kept past this page: the next page that loads the cached campaign shows the new dates. A
      // passed / unreachable window is not, so a brand moving go-live later is seen at once.
      if (window && campaign?.id) latestGoLive.set(campaign.id, window);
      setConfirmedFor(null);
      setFocusRequest((n) => n + 1);
      toast({
        title: 'The go-live dates changed',
        description: window ? `The brand now needs the post ${goLiveLabel(window, 'short')}. Confirm the new dates to continue.` : message,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return true;
    }
    if (window) setServerBlocked({ window, code });
    toast({ title: message || "This campaign can't be accepted any more", status: 'error', duration: 6000, isClosable: true });
    return true;
  };

  const draft = deadlineKindFor(campaign, collab) === 'draft';
  return {
    required,
    goLive,
    labels: goLive && {
      confirm: goLiveLabels(goLive).confirm,
      note: draft
        ? "The brand's go-live dates. Your draft is due first, so the brand can review it before you post."
        : "The brand's go-live dates. Your content must be live then.",
    },
    confirmed,
    passed: passed || ownPassed,
    unreachable,
    changed: !!serverWindow,
    setConfirmed: (checked) => setConfirmedFor(checked && goLive ? goLive : null),
    blockedReason,
    goLiveConfirmed: required && confirmed ? goLive : undefined,
    handleError,
    focusRequest,
  };
};

interface PostByConfirmProps {
  state: PostByConfirmState;
}

/**
 * "I can post on Oct 28" checkbox for go-live campaigns. A locked collab whose own go-live has
 * passed gets only the reason. Renders nothing otherwise.
 */
const PostByConfirm = forwardRef<HTMLInputElement, PostByConfirmProps>(({ state }, ref) => {
  const descriptionId = useId();
  const checkboxRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRefs(ref, checkboxRef);
  // After a GO_LIVE_CHANGED refusal the box appears (or resets) in place: move focus to it.
  useEffect(() => {
    if (!state.focusRequest) return;
    checkboxRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    checkboxRef.current?.focus({ preventScroll: true });
  }, [state.focusRequest]);
  if (!state.required || !state.labels) {
    if (!state.blockedReason) return null;
    return (
      <Box as="section" aria-label="Go-live dates" fontFamily="display" bg="#F2F2F0" borderRadius="14px" px="14px" py="12px">
        <Text fontSize="14px" lineHeight="1.4" fontWeight="600" color="#0E0E0E" role="status">
          {state.blockedReason}
        </Text>
      </Box>
    );
  }
  const { confirmed, setConfirmed, blockedReason, labels } = state;
  return (
    <Box
      as="section"
      aria-label="Go-live date confirmation"
      fontFamily="display"
      color="#0E0E0E"
      bg={confirmed ? '#F1FADF' : '#FFFFFF'}
      border={confirmed ? '2px solid #0E0E0E' : '1px solid #DCDCD8'}
      borderRadius="16px"
      p={confirmed ? '13px' : '14px'}
    >
      <Checkbox
        ref={mergedRef}
        isChecked={confirmed}
        onChange={(e) => setConfirmed(e.target.checked)}
        aria-describedby={descriptionId}
        alignItems="flex-start"
        w="100%"
        minH="22px"
        spacing="12px"
        sx={{
          '.chakra-checkbox__control': {
            w: '22px',
            h: '22px',
            mt: 0,
            borderRadius: '6px',
            border: '2px solid #0E0E0E',
            bg: '#FFFFFF',
            color: '#FFFFFF',
          },
          '.chakra-checkbox__control[data-checked]': { bg: '#0E0E0E', borderColor: '#0E0E0E' },
        }}
      >
        <Text fontSize="15px" lineHeight="1.4" fontWeight="600" color="#0E0E0E">
          {labels.confirm}
        </Text>
      </Checkbox>
      <Text id={descriptionId} fontSize="13px" lineHeight="1.4" color="#55555A" mt="6px" pl="34px" aria-live="polite">
        {blockedReason ?? labels.note}
      </Text>
    </Box>
  );
});
PostByConfirm.displayName = 'PostByConfirm';

export default memo(PostByConfirm);
